import { useEffect, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import { signInWithEmailAndPassword } from "firebase/auth";
import { Form, Input, Button, Message, Link } from "@arco-design/web-react";
import { IconEmail } from "@arco-design/web-react/icon";
import axios from "axios";

import { endpoints } from "../api/endpoints";
import { auth } from "../firebaseConfig";
import {
  AuthSplitLayout,
  AuthHeadline,
  AuthSubtext,
  AuthFormSection,
  AuthFooterLink,
} from "../components/auth/AuthSplitLayout";
import { AuthProgressSteps } from "../components/auth/AuthProgressSteps";
import { AuthSsoRow } from "../components/auth/AuthSsoRow";
import { RoleSelectCard } from "../components/auth/RoleSelectCard";
import { isNycRegistrationMode, NYC_MENTOR_SURVEY_URL } from "../constants/nycRegistration";
import { guestIntakePath, registrationAttributionFromSearchParams } from "../constants/guestIntake";
import { useEventIntakeEnabled } from "../hooks/useEventIntakeEnabled";
import { getButtonColor, getButtonTextColor, getColorAccent1 } from "../utils/cssVariables";
import { routes } from "../routes/routes";
import { RootState } from "../store/store";
import { setUser } from "../store/userSlice";
import { trackSignUp } from "../analytics/track";
import { completeAuthRedirect } from "../utils/authReturnUrl";
import {
  isDuplicateEmailSignUpError,
  isExistingFirebaseSignUpSuccess,
  resolveSignUpErrorMessage,
} from "../utils/signUpError";
import "../components/auth/AuthSplitLayout.css";

const FormItem = Form.Item;

type RegisterStep = "welcome" | "role" | "profile";

const handleSignUpError = (
  error: unknown,
  t: TFunction<"translation", undefined>,
  onDuplicateEmail?: () => void,
) => {
  if (isDuplicateEmailSignUpError(error) && onDuplicateEmail) {
    onDuplicateEmail();
    return;
  }

  const message = resolveSignUpErrorMessage(error, t);
  if (!message) {
    console.error("Unknown signup error:", error);
    Message.error(t("notification_messages.unexpected_error"));
    return;
  }

  Message.error(message);
};

type RegisterRole = "mentor" | "mentee" | "guest";

export const RegisterPreview = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const eventIntakeEnabled = useEventIntakeEnabled();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  const [step, setStep] = useState<RegisterStep>("welcome");
  const [selectedRole, setSelectedRole] = useState<RegisterRole | null>(null);
  const [isEmailVerificationSent, setEmailVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>("");

  const isMentor = selectedRole === "mentor";
  const isGuest = selectedRole === "guest";
  const isNycMode = isNycRegistrationMode();
  const intakeAttribution = registrationAttributionFromSearchParams(searchParams);
  const loginHref = routes.LOGIN;

  const redirectExistingAccountToLogin = (messageKey: string) => {
    Message.info(t(`notification_messages.${messageKey}`));
    navigate(loginHref);
  };

  const redirectDuplicateEmailToLogin = () => {
    redirectExistingAccountToLogin("sign_up_error_tenant_duplicate");
  };

  const redirectReusedFirebaseToLogin = () => {
    redirectExistingAccountToLogin("sign_up_existing_firebase_sign_in");
  };

  useEffect(() => {
    if (!!user) {
      const defaultPage = user.isAdmin ? routes.ADMIN_DASHBOARD : routes.DASHBOARD;
      navigate(defaultPage);
    }
  }, [navigate, user]);

  // Load reCAPTCHA script when configured (backend skips verification when unset).
  useEffect(() => {
    const sitekey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!sitekey) {
      return;
    }

    if (document.querySelector(`script[src*="recaptcha/enterprise.js"]`)) {
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${sitekey}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector(`script[src*="recaptcha/enterprise.js"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const handleResendEmail = async () => {
    try {
      if (!registeredEmail) {
        Message.error(t("email_confirmation.confirmation_resent_error"));
        return;
      }

      await axios.post(endpoints.confirmEmail, { email: registeredEmail });
      Message.success({
        closable: true,
        content: t("email_confirmation.confirmation_resent_success"),
      });
    } catch (error) {
      console.error(error);
      Message.error(t("email_confirmation.confirmation_resent_error"));
    }
  };

  const onMentorSelect = () => {
    if (isNycMode) {
      window.location.href = NYC_MENTOR_SURVEY_URL;
      return;
    }
    setSelectedRole("mentor");
  };

  const onContinueFromRole = () => {
    if (selectedRole === "mentor" && isNycMode) {
      window.location.href = NYC_MENTOR_SURVEY_URL;
      return;
    }
    setStep("profile");
  };

  const onSubmit = async () => {
    try {
      const values = await form.validate();
      const { name, last_name, password } = values;
      const email = String(values.email ?? "").trim();

      const sitekey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      let token = "";
      if (sitekey) {
        const grecaptcha = (window as Window & { grecaptcha?: { enterprise?: { execute: (key: string, opts: { action: string }) => Promise<string> } } }).grecaptcha;
        if (!grecaptcha?.enterprise) {
          Message.error(t("notification_messages.recaptcha_not_loaded"));
          return;
        }

        try {
          token = await grecaptcha.enterprise.execute(sitekey, { action: "signup" });
        } catch (error) {
          console.error("reCAPTCHA execute error:", error);
          Message.error(t("notification_messages.captcha_required"));
          return;
        }

        if (!token) {
          Message.error(t("notification_messages.captcha_required"));
          return;
        }
      }

      const signupPayload: Record<string, unknown> = {
        name,
        last_name,
        password,
        email,
        is_mentor: isGuest ? false : isMentor,
        is_guest: isGuest,
        is_confirmed: isGuest,
        ...(intakeAttribution.source ? { source: intakeAttribution.source } : {}),
      };
      if (token) {
        signupPayload.recaptcha_token = token;
      }

      const signupResponse = await axios.post(endpoints.signupWithEmail, signupPayload);

      if (isGuest && isExistingFirebaseSignUpSuccess(signupResponse.data)) {
        redirectReusedFirebaseToLogin();
        return;
      }

      trackSignUp({
        method: "email",
        role: isGuest ? "guest" : isMentor ? "mentor" : "mentee",
        source: intakeAttribution.source,
      });

      if (isGuest) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const idToken = await userCredential.user.getIdToken();
        const response = await axios.get(endpoints.whoami, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        dispatch(setUser({ ...response.data }));
        completeAuthRedirect(guestIntakePath(intakeAttribution));
        return;
      }

      await axios
        .post(endpoints.confirmEmail, { email })
        .finally(() => {
          setRegisteredEmail(email);
          setEmailVerificationSent(true);
        });
    } catch (error) {
      handleSignUpError(error, t, isGuest ? redirectDuplicateEmailToLogin : undefined);
    }
  };

  if (isEmailVerificationSent) {
    return (
      <AuthSplitLayout>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <IconEmail style={{ fontSize: 32, stroke: getColorAccent1() }} />
          <span style={{ fontSize: 20, color: getColorAccent1() }} className="alice-regular">
            {t("email_confirmation.email_confirmation")}
          </span>
        </div>
        <AuthSubtext>{t("register_page.thanks_for_registration")}</AuthSubtext>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", marginTop: 24 }}>
          <Button
            onClick={() => navigate(routes.LOGIN)}
            size="large"
            shape="round"
            className="auth-primary-btn"
          >
            {t("register_page.back_to_login")}
          </Button>
          <Button
            style={{ color: getButtonTextColor(), backgroundColor: getButtonColor() }}
            size="large"
            shape="round"
            onClick={handleResendEmail}
          >
            {t("email_confirmation.resend_confirmation")}
          </Button>
        </div>
      </AuthSplitLayout>
    );
  }

  if (step === "welcome") {
    return (
      <AuthSplitLayout>
        <AuthHeadline>{t("register_page.welcome_headline")}</AuthHeadline>
        <AuthSubtext>{t("register_page.welcome_subtext")}</AuthSubtext>
        <Button
          className="auth-primary-btn"
          size="large"
          onClick={() => setStep("role")}
        >
          {t("register_page.get_started_button")}
        </Button>
        <AuthProgressSteps currentStep={1} />
        <AuthFooterLink>
          <NavLink to={routes.LOGIN}>{t("register_page.back_to_login")}</NavLink>
        </AuthFooterLink>
      </AuthSplitLayout>
    );
  }

  if (step === "role") {
    return (
      <AuthSplitLayout>
        <AuthHeadline>{t("register_page.choose_role_headline")}</AuthHeadline>
        <AuthSubtext>{t("register_page.choose_role_subtext")}</AuthSubtext>

        {isNycMode && (
          <p
            style={{
              margin: "0 0 16px",
              fontSize: 16,
              fontWeight: "bold",
              color: "black",
              wordBreak: "break-word",
              width: "100%",
            }}
          >
            {t("register_page.advisor_redirect_notice")}
          </p>
        )}

        <div className="auth-role-cards">
          <RoleSelectCard
            role="mentor"
            selected={selectedRole === "mentor"}
            title={t("common.mentor")}
            description={t("register_page.mentor_card_description")}
            onSelect={onMentorSelect}
          />
          <RoleSelectCard
            role="mentee"
            selected={selectedRole === "mentee"}
            title={t("common.mentee")}
            description={t("register_page.mentee_card_description")}
            onSelect={() => setSelectedRole("mentee")}
          />
          {eventIntakeEnabled ? (
            <RoleSelectCard
              role="mentee"
              selected={selectedRole === "guest"}
              title={t("register_page.guest_button_label")}
              description={t("register_page.guest_card_description")}
              onSelect={() => setSelectedRole("guest")}
            />
          ) : null}
        </div>

        <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
          <Button
            className="auth-primary-btn auth-primary-btn--inline"
            size="large"
            disabled={!selectedRole}
            onClick={onContinueFromRole}
          >
            {t("register_page.continue_button")}
          </Button>
        </div>

        <AuthProgressSteps currentStep={2} />
        <AuthFooterLink>
          <NavLink to={routes.LOGIN}>{t("register_page.back_to_login")}</NavLink>
        </AuthFooterLink>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout>
      <AuthHeadline>
        {isGuest
          ? t("register_page.profile_guest_headline")
          : isMentor
          ? t("register_page.profile_mentor_headline")
          : t("register_page.profile_mentee_headline")}
      </AuthHeadline>

      <AuthFormSection className="auth-profile-step">
        <AuthSsoRow
          mode="register"
          isMentor={isMentor}
          isGuest={isGuest}
          signupSource={intakeAttribution.source ?? undefined}
          redirectPath={isGuest ? guestIntakePath(intakeAttribution) : undefined}
        />

        <div className="auth-profile-card">
          <Form form={form} layout="vertical" onSubmit={onSubmit} scrollToFirstError>
            <div className="auth-profile-section">{t("register_page.section_account")}</div>
            <FormItem
              className="auth-underline-field"
              field="name"
              label={t("register_page.name_label")}
              rules={[
                {
                  required: true,
                  message: t("register_page.name_placeholder"),
                  validator: (v, cb) =>
                    !v || v.trim().length === 0
                      ? cb(t("register_page.name_placeholder"))
                      : cb(),
                },
              ]}
            >
              <Input placeholder={t("register_page.name_placeholder")} autoComplete="off" />
            </FormItem>
            <FormItem
              className="auth-underline-field"
              field="last_name"
              label={t("register_page.last_name_label")}
              rules={[
                {
                  required: true,
                  message: t("register_page.last_name_placeholder"),
                  validator: (v, cb) =>
                    !v || v.trim().length === 0
                      ? cb(t("register_page.last_name_placeholder"))
                      : cb(),
                },
              ]}
            >
              <Input placeholder={t("register_page.last_name_placeholder")} autoComplete="off" />
            </FormItem>
            <FormItem
              className="auth-underline-field"
              field="email"
              label={t("register_page.email_label")}
              rules={[
                { required: true, message: t("form_validation_messages.empty_email") },
                {
                  validator: (v, cb) => {
                    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                    if (!v || v.trim().length === 0) {
                      cb(t("form_validation_messages.empty_email"));
                    } else if (v.length > 100) {
                      cb(t("form_validation_messages.long_email"));
                    } else if (!emailRegex.test(v)) {
                      cb(t("form_validation_messages.invalid_email"));
                    } else {
                      cb();
                    }
                  },
                },
              ]}
            >
              <Input type="email" placeholder={t("register_page.email_placeholder")} autoComplete="off" />
            </FormItem>
            <FormItem
              className="auth-underline-field"
              field="password"
              label={t("register_page.password_label")}
              rules={[
                { required: true, message: t("form_validation_messages.no_password") },
                { minLength: 6, message: t("form_validation_messages.short_password") },
                {
                  validator: (v, cb) => {
                    if (!v || v.trim().length === 0) {
                      cb(t("form_validation_messages.invalid_password"));
                    } else {
                      cb();
                    }
                  },
                },
              ]}
            >
              <Input.Password placeholder={t("register_page.password_placeholder")} autoComplete="off" />
            </FormItem>

            <div className="auth-profile-section">{t("register_page.section_personal")}</div>
            <FormItem className="auth-underline-field" field="linkedIn" label={t("profile.linkedin_label")}>
              <Input placeholder={t("profile.linkedin_placeholder")} />
            </FormItem>
            <FormItem className="auth-underline-field" field="website" label={t("profile.website_label")}>
              <Input placeholder={t("profile.website_placeholder")} />
            </FormItem>

            {isMentor && !isGuest && (
              <>
                <div className="auth-profile-section">{t("register_page.section_professional")}</div>
                <FormItem
                  className="auth-underline-field"
                  field="shortDescription"
                  label={t("register_page.professional_title_label")}
                >
                  <Input placeholder={t("register_page.professional_title_placeholder")} />
                </FormItem>
                <FormItem
                  className="auth-underline-field"
                  field="company"
                  label={t("register_page.company_label")}
                >
                  <Input placeholder={t("register_page.company_placeholder")} />
                </FormItem>
                <FormItem
                  className="auth-underline-field"
                  field="industry"
                  label={t("register_page.industry_label")}
                >
                  <Input placeholder={t("register_page.industry_placeholder")} />
                </FormItem>
                <FormItem
                  className="auth-underline-field"
                  field="subIndustry"
                  label={t("register_page.sub_industry_label")}
                >
                  <Input placeholder={t("register_page.sub_industry_placeholder")} />
                </FormItem>
              </>
            )}

            <div className="auth-profile-section">{t("register_page.section_about")}</div>
            <FormItem
              className="auth-underline-field"
              field="shortBio"
              label={t("register_page.short_description_label")}
            >
              <Input placeholder={t("register_page.short_description_placeholder")} />
            </FormItem>
            <FormItem
              className="auth-underline-field"
              field="fullBio"
              label={t("profile.bio_label")}
            >
              <Input.TextArea placeholder={t("profile.bio_placeholder")} rows={3} />
            </FormItem>
          </Form>
        </div>

        <div style={{ fontSize: 13, marginBottom: 16, textAlign: "center" }}>
          {t("register_page.agreement")}
          <Link href="https://we-rule.com/terms-conditions" target="_blank">
            {t("register_page.terms")}
          </Link>
        </div>

        <div className="auth-profile-actions">
          <Button type="text" onClick={() => setStep("role")}>
            {t("register_page.go_back")}
          </Button>
          <Button className="auth-primary-btn auth-primary-btn--inline" size="large" onClick={onSubmit}>
            {t("register_page.finalize_button")}
          </Button>
        </div>

        <AuthProgressSteps currentStep={3} />
      </AuthFormSection>
    </AuthSplitLayout>
  );
};
